import { EngagementBuyerValueRange } from '../models/engagement-buyer-value-range.model';

export type FormatValueRange = (minPrice: number, maxPrice: number) => string;

// #2077: a Pacific business day as the API sends it ("2026-10-14") in the US form ("10/14/2026"). Read as a Date it would
// shift to the viewer's time zone, so it is only reordered.
export const formatBusinessDay = (businessDayPt: string): string => {
    const [year, month, day] = businessDayPt.split('-');
    return `${month}/${day}/${year}`;
};

// #2077: one tooltip line per range - the range, the day it was given and, when a suspension came after it, that
// suspension's day.
export const getValueRangeLine = (range: EngagementBuyerValueRange, formatRange: FormatValueRange): string =>
    [
        formatRange(range.minPrice, range.maxPrice),
        formatBusinessDay(range.givenOnPt),
        range.beforeTheSuspensionOnPt
            ? `before the suspension on ${formatBusinessDay(range.beforeTheSuspensionOnPt)}`
            : null,
    ]
        .filter((part) => part != null)
        .join(' · ');

export interface ValueRangesOfStep {
    // The newest range the Buyer gave on the step - the one its cell shows - or null when they gave none there.
    shown: EngagementBuyerValueRange | null;
    // Every range they gave before it, on any range step, oldest first.
    earlier: EngagementBuyerValueRange[];
}

// #2077: the API lists every range a Buyer gave, oldest first, each naming its step by action id (every run of a step
// under the same id). The newest range given on any of the steps is the one shown - a step redone after a break keeps the
// one given on it before, never an older one; whatever came before it is earlier.
export const getValueRangesOfSteps = (
    valueRanges: EngagementBuyerValueRange[] | undefined,
    actionIds: number[]
): ValueRangesOfStep => {
    const ranges = valueRanges ?? [];
    const newestIndex = ranges.map((range) => actionIds.includes(range.actionId)).lastIndexOf(true);
    return newestIndex === -1
        ? { shown: null, earlier: [] }
        : { shown: ranges[newestIndex], earlier: ranges.slice(0, newestIndex) };
};

export const getValueRangesOfStep = (
    valueRanges: EngagementBuyerValueRange[] | undefined,
    actionId: number
): ValueRangesOfStep => getValueRangesOfSteps(valueRanges, [actionId]);

// #2077: the newest range the Buyer gave on any range step, and every other one before it - for a view that shows one
// range per Buyer (the DFM buyer selection), where a catch-up's own range step may not have started yet.
export const getNewestValueRange = (valueRanges: EngagementBuyerValueRange[] | undefined): ValueRangesOfStep =>
    getValueRangesOfSteps(valueRanges, (valueRanges ?? []).map((range) => range.actionId));

// #2077: the tooltip of a cell showing the Buyer's own range on the step: the ranges they gave before it, or nothing.
export const getEarlierRangesTooltip = (earlier: EngagementBuyerValueRange[], formatRange: FormatValueRange): string | null =>
    earlier.length === 0
        ? null
        : ['Earlier ranges:', ...earlier.map((range) => getValueRangeLine(range, formatRange))].join('<br />');

// #2077: the tooltip of a cell showing the range a Buyer gave on the step before a break, while its copy after the
// break still waits for a new one: every range they gave, the one shown last. A Buyer who gave only that one range
// gets no tooltip, as everywhere else.
export const getRangeBeforeTheBreakTooltip = (
    { shown, earlier }: ValueRangesOfStep,
    formatRange: FormatValueRange
): string | null =>
    shown == null || earlier.length === 0
        ? null
        : [
              shown.beforeTheSuspensionOnPt
                  ? `No new range since the suspension on ${formatBusinessDay(shown.beforeTheSuspensionOnPt)} — showing the latest one given.`
                  : 'Showing the latest range given.',
              ...[...earlier, shown].map((range) => getValueRangeLine(range, formatRange)),
          ].join('<br />');

// #943 + #2077: the tooltip of a cell carrying a range forward from an earlier step the Buyer gave no update on: the
// headline, then those earlier ranges, oldest first, with their days.
export const getCarriedForwardTooltip = (
    valueRanges: EngagementBuyerValueRange[] | undefined,
    earlierStepActionIds: number[],
    formatRange: FormatValueRange
): string =>
    [
        'No update submitted — showing initial range.',
        ...(valueRanges ?? [])
            .filter((range) => earlierStepActionIds.includes(range.actionId))
            .map((range) => getValueRangeLine(range, formatRange)),
    ].join('<br />');
